import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Package, PlusSquare, CheckCircle2, Clock, Eye, Layers, AlertTriangle, TrendingDown } from "lucide-react";
import { CountTile, centsToUsd, ExportButton, EmptyNote, BarList } from "./shared";

export default function ListingsTab({ force }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = force ? "?_force_refresh=1" : "";
    api.get(`/admin/analytics/listings-performance${q}`).then(({ data }) => setData(data)).catch(() => {});
  }, [force]);

  const k = data?.kpis;

  return (
    <div data-testid="listings-tab">
      <div className="flex items-center justify-end gap-3 mb-4">
        <ExportButton path="/admin/analytics/export/listings.csv" filename="opticswap_listings.csv" testId="export-listings" />
      </div>

      {/* KPIs */}
      {k && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="listings-kpis">
          <CountTile icon={Package} label="Active Listings" value={k.total_active} />
          <CountTile icon={PlusSquare} label="New Listings (30 days)" value={k.new_30d} />
          <CountTile icon={CheckCircle2} label="Listings Sold (30 days)" value={k.sold_30d} />
          <div className={`border bg-surface p-5 ${k.warnings?.avg_time_to_sale_days ? "border-blaze/60" : "border-sniper"}`} data-testid="tile-average-time-to-sale">
            <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Average Time to Sale
            </span>
            <div className={`font-heading text-3xl mt-2 ${k.warnings?.avg_time_to_sale_days ? "text-blaze" : "text-foreground"}`}>
              {k.avg_time_to_sale_days == null ? "—" : `${k.avg_time_to_sale_days} days`}
            </div>
            {k.warnings?.avg_time_to_sale_days && (
              <p className="text-[10px] font-mono text-blaze mt-1 leading-snug flex items-start gap-1" data-testid="kpi-warning-avg-time-to-sale">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={2} /> {k.warnings.avg_time_to_sale_days}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Most viewed */}
      <h2 className="font-heading text-2xl uppercase tracking-tight mt-10 mb-3 text-foreground flex items-center gap-2"><Eye className="w-5 h-5 text-olive-bright" /> Most-Viewed Listings</h2>
      <div className="border border-sniper bg-surface overflow-x-auto" data-testid="most-viewed-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-[10px] uppercase text-muted-foreground border-b border-sniper">
              <th className="text-left p-3">Listing</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Views</th>
              <th className="text-right p-3">Messages</th>
              <th className="text-right p-3">View → Message %</th>
            </tr>
          </thead>
          <tbody>
            {(data?.most_viewed || []).length === 0 && <tr><td colSpan={5}><EmptyNote>No listings yet.</EmptyNote></td></tr>}
            {(data?.most_viewed || []).map((l) => (
              <tr key={l.listing_id} className="border-b border-sniper last:border-0" data-testid={`mv-${l.listing_id}`}>
                <td className="p-3 font-body text-foreground truncate max-w-[260px]">{l.title}</td>
                <td className="p-3 font-mono text-xs text-secondary-foreground">{l.category}</td>
                <td className="p-3 text-right font-mono text-foreground">{l.views}</td>
                <td className="p-3 text-right font-mono text-secondary-foreground">{l.messages}</td>
                <td className="p-3 text-right font-mono text-olive-bright">{l.view_to_message_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Conversion by category */}
      <h2 className="font-heading text-2xl uppercase tracking-tight mt-10 mb-3 text-foreground">Listing-to-Sale Conversion by Category</h2>
      <p className="text-[11px] font-mono uppercase text-muted-foreground mb-3">% of listings (last 120 days) sold within 30 days of posting</p>
      <BarList testId="conversion-by-category"
        rows={(data?.conversion_by_category || []).map((c) => ({ label: c.category, value: c.conversion_pct, pct: null, extra: c, warning: c.warning || null }))}
        valueFmt={(v, r) => `${v}%  (${r.extra.sold}/${r.extra.created})`} />

      {/* Category performance */}
      <h2 className="font-heading text-2xl uppercase tracking-tight mt-10 mb-3 text-foreground flex items-center gap-2"><Layers className="w-5 h-5 text-olive-bright" /> Category Performance</h2>
      <div className="border border-sniper bg-surface overflow-x-auto" data-testid="category-performance-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-[10px] uppercase text-muted-foreground border-b border-sniper">
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Active</th>
              <th className="text-right p-3">Views</th>
              <th className="text-right p-3">Messages</th>
              <th className="text-right p-3">Sales</th>
              <th className="text-right p-3">Average Sale</th>
            </tr>
          </thead>
          <tbody>
            {(data?.category_performance || []).length === 0 && <tr><td colSpan={6}><EmptyNote>No data yet.</EmptyNote></td></tr>}
            {(data?.category_performance || []).map((c) => (
              <tr key={c.category} className="border-b border-sniper last:border-0" data-testid={`cat-${c.category}`}>
                <td className="p-3 font-body text-foreground">{c.category}</td>
                <td className="p-3 text-right font-mono text-foreground">{c.active}</td>
                <td className="p-3 text-right font-mono text-secondary-foreground">{c.views}</td>
                <td className="p-3 text-right font-mono text-secondary-foreground">{c.messages}</td>
                <td className="p-3 text-right font-mono text-olive-bright">{c.sales}</td>
                <td className="p-3 text-right font-mono text-volt">{c.avg_sale_price_cents ? centsToUsd(c.avg_sale_price_cents) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stale + unmet demand */}
      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-3 text-foreground flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-blaze" /> Stale Listings</h2>
          <p className="text-[11px] font-mono uppercase text-muted-foreground mb-3">Active &gt; 30 days · zero messages</p>
          <div className="border border-sniper bg-surface divide-y divide-sniper max-h-[320px] overflow-y-auto" data-testid="stale-listings">
            {(data?.stale_listings || []).length === 0 && <EmptyNote>Nothing stale — every active listing has engagement.</EmptyNote>}
            {(data?.stale_listings || []).map((l) => (
              <div key={l.listing_id} className="flex items-center justify-between p-2.5 text-sm">
                <span className="font-body text-secondary-foreground truncate pr-3">{l.title}</span>
                <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">{l.views} views</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-3 text-foreground flex items-center gap-2"><TrendingDown className="w-5 h-5 text-blaze" /> Unmet Demand</h2>
          <p className="text-[11px] font-mono uppercase text-muted-foreground mb-3">Zero-result searches (30 days) — demand with no supply</p>
          <div className="border border-blaze/40 bg-surface divide-y divide-sniper max-h-[320px] overflow-y-auto" data-testid="unmet-demand">
            {(data?.unmet_demand || []).length === 0 && <EmptyNote>No zero-result searches yet.</EmptyNote>}
            {(data?.unmet_demand || []).map((t) => (
              <div key={t.term} className="flex items-center justify-between p-2.5 font-mono text-sm">
                <span className="text-blaze truncate pr-3">{t.term}</span>
                <span className="text-foreground">{t.count}×</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="h-10" />
    </div>
  );
}
